import React, { useState, useEffect } from 'react';

import { MultiSelect, MultiSelectProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteLocations from 'hooks/location/useAutoCompleteLocations';
import { Location } from 'types';

export interface ILocationMultiSelectAutoComplete extends Pick<Location, 'locationId' | 'name'> {}

interface LocationMultiSelectAutoCompleteProps extends Omit<MultiSelectProps, 'data'> {
  value?: string[];
  onChange?: (values: string[]) => void;
}

const LocationMultiSelectAutoComplete = (props: LocationMultiSelectAutoCompleteProps): JSX.Element => {
  const { value, onChange, ...msProps } = props;
  const [autocompleteData, setAutocompleteData] = useState<ILocationMultiSelectAutoComplete[]>([]);
  const [locationSearch, setLocationSearch] = useState<string>('');
  const [debouncedLocationSearchTerm] = useDebouncedValue<string>(locationSearch, 250);
  const { data, isSuccess } = useAutoCompleteLocations({ search: debouncedLocationSearchTerm });
  const [selectedLocations, setSelectedLocations] = useState<ILocationMultiSelectAutoComplete[]>([]);

  const handleSearchChange = (search: string): void => {
    setLocationSearch(search);
  };

  useEffect(() => {
    if (isSuccess && data) {
      let results = data.data.results as Pick<Location, 'locationId' | 'name'>[];
      results = [{ locationId: 'noLocation', name: 'No Location' }].concat(results);
      const updatedData = results.concat(
        selectedLocations.filter(
          selectedLocation => !results.some(loc => loc.locationId === selectedLocation.locationId),
        ),
      );
      setAutocompleteData(updatedData);
    }
  }, [isSuccess, data, selectedLocations]);

  const handleChange = (selectedItems: string[]): void => {
    if (onChange) {
      onChange(selectedItems);
    }

    const newSelectedLocations = autocompleteData.filter(location =>
      selectedItems.includes(location.locationId),
    );
    setSelectedLocations(newSelectedLocations);
  };

  return (
    <MultiSelect
      {...msProps}
      value={value}
      onChange={handleChange}
      data={autocompleteData.map(location => ({
        value: location.locationId,
        label: location.name,
      }))}
      clearable
      searchable
      placeholder='Select locations'
      nothingFound='No locations found'
      onSearchChange={handleSearchChange}
    />
  );
};

export default LocationMultiSelectAutoComplete;
